import { useEffect, useState, useCallback } from "react";
import { db } from "../services/firebase";
import { ref, onValue, get, child, off, set, remove, update } from "firebase/database";

export default function useManageSelectedInterviewQuestions(domainId, packId, interviewId) {

    const [questions, setQuestions] = useState([]);
    const [selectedQuestions, setSelectedQuestions] = useState({});
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (!domainId || !packId || !interviewId) return;

        const interviewRef = ref(db, `InterviewQuestions/${domainId}/${packId}/${interviewId}`);

        const unsubscribe = onValue(interviewRef, async (snapshot) => {
            const iqData = snapshot.val();
            if (!iqData) {
                setQuestions([]);
                setLoading(false);
                return;
            }

            // 1️⃣ Extract question IDs and orderIds from InterviewQuestions
            const ordered = Object.entries(iqData)
                .map(([qid, info]) => ({ id: qid, orderId: info?.orderId ?? 0 }))
                .sort((a, b) => (a.orderId ?? 0) - (b.orderId ?? 0));

            // 2️⃣ Fetch full question details
            const fetches = ordered.map(async ({ id, orderId }) => {
                const qSnap = await get(child(ref(db), `Questions/AllQuestions/${domainId}/${id}`));
                if (!qSnap.exists()) return null;
                const qData = qSnap.val();

                // ✅ Preserve InterviewQuestions.orderId, not overwritten by Questions data
                return { id, ...qData, orderId };
            });

            const fullQuestions = (await Promise.all(fetches)).filter(Boolean);

            // 3️⃣ Strictly keep order as InterviewQuestions.orderId
            fullQuestions.sort((a, b) => (a.orderId ?? 0) - (b.orderId ?? 0));

            setQuestions(fullQuestions);
            setLoading(false);
        });

        return () => {
            off(interviewRef);
            if (typeof unsubscribe === "function") unsubscribe();
        };
    }, [domainId, packId, interviewId]);


    // Fetch selected interview questions
    useEffect(() => {
        if (!domainId || !packId || !interviewId) return;
        const selectedRef = ref(db, `InterviewQuestions/${domainId}/${packId}/${interviewId}`);
        const unsubscribe = onValue(selectedRef, (snapshot) => {
            const data = snapshot.val();
            setSelectedQuestions(data || {});
        });
        return () => unsubscribe();
    }, [domainId, packId, interviewId]);

    // Toggle question selection
    const toggleQuestion = useCallback(
        async (questionId) => {
            const isSelected = !!selectedQuestions?.[questionId];
            const qRef = ref(db, `InterviewQuestions/${domainId}/${packId}/${interviewId}/${questionId}`);
            if (isSelected) {
                await remove(qRef);
            } else {
                await set(qRef, { selected: true, orderId: 0 });
            }
        },
        [domainId, packId, interviewId, selectedQuestions]
    );

    // Update orderId (with duplicate check)
    const updateOrderId = useCallback(
        async (questionId, newOrderId) => {
            if (!questionId || newOrderId === undefined || newOrderId === "") return;

            const numericId = Number(newOrderId);
            if (isNaN(numericId)) {
                alert("⚠️ Order ID must be a number");
                return;
            }

            // Check for duplicates among selected questions
            const duplicate = Object.entries(selectedQuestions).find(
                ([id, val]) => id !== questionId && val?.orderId === numericId
            );

            if (duplicate) {
                alert(`⚠️ Duplicate order ID "${numericId}" already exists for another question.`);
                return;
            }

            // Update Firebase
            const qRef = ref(db, `InterviewQuestions/${domainId}/${packId}/${interviewId}/${questionId}`);
            await update(qRef, { orderId: numericId });
        },
        [domainId, packId, interviewId, selectedQuestions]
    );

    const selectedIds = new Set(Object.keys(selectedQuestions || {}));

    return {
        questions,
        selectedIds,
        selectedQuestions,
        loading,
        toggleQuestion,
        updateOrderId,
    };
}
